import React, { useEffect, useRef } from "react";

const PIXEL_COUNT = 190;

// The wordmark is cyan and the interface signal is green. The falling pixels
// travel between the two, so the logo and the UI read as one system.
const HUE_START = 187; // wordmark cyan
const HUE_END = 158; // signal green

/**
 * Pixels shedding off the wordmark, the site's signature. Drawn on a fixed
 * canvas above the page, never interactive, skipped entirely under
 * prefers-reduced-motion, and paused whenever the tab is hidden.
 */
const PixelRain: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let raf = 0;
    let running = true;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let source = { cx: w * 0.82, bottom: 56, halfW: 30 };

    const readSource = () => {
      const el = document.getElementById("site-logo");
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0) return;
      source = {
        // The pixel trail in the artwork sits under "Digital", the left half
        // of the lockup, so the rain starts there rather than dead centre.
        cx: r.left + r.width * 0.62,
        bottom: r.bottom - r.height * 0.1,
        halfW: r.width * 0.2,
      };
    };
    readSource();

    interface Px {
      x: number;
      y: number;
      s: number;
      vx: number;
      vy: number;
      a: number;
      rot: number;
      rs: number;
      g: number;
      gs: number;
    }

    const spawn = (px?: Px): Px => {
      const p = px ?? ({} as Px);
      p.x = source.cx + (Math.random() - 0.5) * source.halfW * 2;
      p.y = source.bottom + Math.random() * 4;
      p.s = Math.random() * 4 + 1.6;
      p.vx = 0;
      p.vy = Math.random() * 0.3 + 0.08;
      p.a = Math.random() * 0.4 + 0.28;
      p.rot = (Math.random() - 0.5) * 0.3;
      p.rs = (Math.random() - 0.5) * 0.006;
      p.g = Math.random() * Math.PI * 2;
      p.gs = Math.random() * 0.018 + 0.006;
      return p;
    };

    const pixels: Px[] = [];
    for (let i = 0; i < PIXEL_COUNT; i++) {
      const p = spawn();
      // Seed the column already populated, and bias the distribution toward the
      // wordmark so the trail reads dense where the logo sheds it and thins out
      // as it falls, the way the artwork itself is drawn.
      const t = Math.random() ** 2.4;
      p.y = source.bottom + t * h * 0.85;
      p.x = source.cx + (Math.random() - 0.5) * source.halfW * 2 * (1 + t * t * 4);
      p.a *= 1 - t * 0.5;
      pixels.push(p);
    }

    const FALL = () => h * 0.85;

    const draw = () => {
      readSource();
      ctx.clearRect(0, 0, w, h);

      const span = FALL();

      for (const p of pixels) {
        p.y += p.vy;
        p.rot += p.rs;
        p.g += p.gs;

        const t = Math.min(Math.max(p.y - source.bottom, 0) / span, 1);

        p.vx += (Math.random() - 0.5) * t * t * 0.018;
        p.vx *= 0.994;
        p.x += p.vx;

        p.vy = Math.max(0.06, Math.min(0.55, p.vy + (Math.random() - 0.5) * 0.002));

        if (p.y > h + 24 || p.x < -40 || p.x > w + 40) {
          spawn(p);
          continue;
        }

        const alpha = p.a * (1 - t * 0.62) * (0.55 + 0.45 * Math.sin(p.g));
        if (alpha < 0.006) continue;

        // Cyan at the wordmark, signal green by the time it has fallen.
        const hue = HUE_START + (HUE_END - HUE_START) * t;
        const sz = p.s * (1 - t * 0.25);

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = `hsla(${hue}, 84%, 58%, ${alpha})`;
        ctx.fillRect(-sz / 2, -sz / 2, sz, sz);
        ctx.fillStyle = `hsla(${hue}, 90%, 78%, ${alpha * 0.45})`;
        ctx.fillRect(-sz / 2, -sz / 2, sz, 1);
        ctx.restore();
      }

      if (running) raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[45] h-full w-full"
    />
  );
};

export default PixelRain;
